class CustomDiscountBox extends HTMLElement {
  constructor() {
    super();
    this.codeComponentSkeleton = this.parseHTML(
      this.getAttribute('data-code-skeleton'),
    );
    this.privateToken = this.getAttribute('data-sf-token');
    this.inputCode = this.querySelector('#DiscountCode');
    this.addBtn = this.querySelector('.custom_discount_box-submit');
    this.codeList = this.querySelector('.custom_discount_box-code-list');
    this.addBtn?.addEventListener('click', this.onBtnClick);
    this.inputCode?.addEventListener('keydown', this.onInputChange);
    this.discountCodesDB = new SessionDiscountCodeStorage();
    this.initCartObserver();
    this.checkCart();
  }

  checkCart = async () => {
    const cartJs = await this.getCartJs();
    if (!cartJs) {
      return;
    }

    const cartCodes = cartJs.discount_codes.filter(
      (discountCode) => discountCode.applicable,
    );

    cartJs.items.forEach((item) => {
      if (!item.line_level_discount_allocations.length) {
        return;
      }
      item.line_level_discount_allocations.forEach((discount_allocation) => {
        const targetCode = cartCodes.find(
          (cartCode) =>
            cartCode.code.toLocaleUpperCase() ===
            discount_allocation.discount_application.title.toLocaleUpperCase(),
        );
        if (targetCode) {
          const index = cartCodes.indexOf(targetCode);

          targetCode.amount =
            (targetCode.amount ?? 0) + discount_allocation.amount;

          cartCodes[index] = targetCode;
        } else {
          cartCodes.push({
            amount: discount_allocation.amount,
            applicable: true,
            code: discount_allocation.discount_application.title.toLocaleUpperCase(),
            removable: false,
          });
        }
      });
    });

    cartJs.cart_level_discount_applications.forEach(
      (cart_level_discount_application) => {
        const targetCode = cartCodes.find(
          (cartCode) =>
            cartCode.code.toLocaleUpperCase() ===
            cart_level_discount_application.title.toLocaleUpperCase(),
        );
        if (targetCode && !targetCode.removable) {
          const index = cartCodes.indexOf(targetCode);

          targetCode.amount =
            (targetCode.amount ?? 0) +
            cart_level_discount_application.total_allocated_amount;

          cartCodes[index] = targetCode;
        } else {
          cartCodes.push({
            amount: cart_level_discount_application.total_allocated_amount,
            applicable: true,
            code: cart_level_discount_application.title.toLocaleUpperCase(),
            removable: false,
          });
        }
      },
    );

    this.setCodeList({ codes: cartCodes });
  };

  onBtnClick = async () => {
    const el = this.addBtn;
    el.classList.add('btn--loading');
    const code = this.inputCode.value.toLocaleUpperCase();
    if (!code.length) {
      el.classList.remove('btn--loading');
      return;
    }
    const cartJs = await this.getCartJs();
    if (!cartJs) {
      el.classList.remove('btn--loading');
      return;
    }
    const cartCodes = cartJs.discount_codes.filter(
      (discountCode) => discountCode.applicable,
    );
    const newCodes = cartCodes.map((cartCode) => cartCode.code);
    newCodes.push(code);
    const responseCodes = await this.updateCartCodes({
      codes: newCodes,
    });

    if (
      !responseCodes.some(
        (codeRes) => codeRes.code === code && codeRes.applicable,
      )
    ) {
      this.setCodeError();
    } else {
      this.discountCodesDB?.register([
        { code: code, status: 'manually-added' },
      ]);
      this.inputCode.value = '';
      document.dispatchEvent(new Event('cart:build'));
      this.checkCart();
    }
    el.classList.remove('btn--loading');
  };

  onInputChange = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.onBtnClick();
    }
  };

  setCodeError = () => {
    const fieldSet = this.querySelector('.custom_discount_box-fieldset');
    if (!fieldSet) {
      return;
    }
    fieldSet.classList.add('custom_discount_box-fieldset-error');
    setTimeout(
      () => fieldSet.classList.remove('custom_discount_box-fieldset-error'),
      5000,
    );
  };

  onRemoveCode = async (e, code) => {
    const el = e.currentTarget;
    el.classList.add('remover--loading');
    const cartJs = await this.getCartJs();
    if (!cartJs) {
      el.classList.remove('remover--loading');
      return;
    }
    const cartCodes = cartJs.discount_codes.filter(
      (discountCode) => discountCode.applicable,
    );
    const newCodes = cartCodes
      .filter((cartCode) => cartCode.code !== code)
      .map((cartCode) => cartCode.code);
    this.discountCodesDB?.register([
      { code: code, status: 'manually-removed' },
    ]);
    await this.updateCartCodes({
      codes: newCodes,
    });
    el.classList.remove('remover--loading');
    document.dispatchEvent(new Event('cart:build'));
    this.checkCart();
  };

  getCartData = async ({ id }) => {
    const query = this.storeFrontQueries({ query: 'getCartData' });
    const variables = {
      id: `gid://shopify/Cart/${id}`,
      country: window?.Shopify?.country || 'SV',
    };
    const response = await this.fetchStorefront({ query, variables });
    return response?.cart || null;
  };

  // updateCartCodes = async ({ id, codes }) => {
  //   const query = this.storeFrontQueries({ query: 'updateCodes' });
  //   const variables = {
  //     cartId: `gid://shopify/Cart/${id}`,
  //     discountCodes: codes,
  //     country: window?.Shopify?.country || 'SV',
  //   };
  //   const response = await this.fetchStorefront({ query, variables });
  //   return response.cartDiscountCodesUpdate?.cart?.discountCodes || null;
  // };

  updateCartCodes = async ({ codes }) => {
    if (!codes) {
      return false;
    }
    const formData = { discount: codes.join(',') || '' };
    try {
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      }).then((res) => res.json());

      return updateResponse?.discount_codes || null;
    } catch (e) {
      return null;
    }
  };

  getCartJs = async () => {
    try {
      return (await fetch('/cart.js').then((res) => res.json())) || null;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  storeFrontQueries = ({ query }) => {
    switch (query) {
      case 'getCartData':
        return `query getCart($id: ID!, $country: CountryCode!) @inContext(country: $country) { cart(id: $id) { id discountCodes { applicable code } cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } } lines(first: 250) { edges { node { id attributes { key value } cost { amountPerQuantity { amount currencyCode } compareAtAmountPerQuantity { amount currencyCode } subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } } quantity merchandise { ... on ProductVariant { id } } } } pageInfo { endCursor hasNextPage } } } }`;
      case 'updateCodes':
        return `mutation updateCartDiscountCodes($cartId: ID!, $discountCodes: [String!], $country: CountryCode!) @inContext(country: $country) { cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) { cart { id discountCodes { applicable code } cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } } lines(first: 250) { edges { node { id attributes { key value } cost { amountPerQuantity { amount currencyCode } compareAtAmountPerQuantity { amount currencyCode } subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } } quantity merchandise { ... on ProductVariant { id } } } } pageInfo { endCursor hasNextPage } } } userErrors { field message } } }`;
      default:
        return null;
    }
  };

  fetchStorefront = async ({ query, variables = undefined }) => {
    try {
      const targetUrl = window?.Shopify?.shop
        ? `https://${window.Shopify.shop}/api/2025-01/graphql.json`
        : '/api/2025-01/graphql.json';
      const response = await fetch(targetUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.privateToken,
        },
        body: JSON.stringify({
          query: query,
          variables: variables,
        }),
      }).then((response) => response.json());
      return response.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  setCodeList = ({ codes }) => {
    this.codeList.innerHTML = '';
    codes.forEach((code) => {
      const newCodeComponent = this.generateCodeComponent({
        code: code.code,
        amount: code.amount
          ? this.convertToMoney({ amount: code.amount })
          : null,
        removable: code.removable === false ? false : true,
      });
      this.codeList.appendChild(newCodeComponent);
    });
  };

  generateCodeComponent = ({ code, amount, removable = true }) => {
    const codeEl = this.codeComponentSkeleton.cloneNode(true);
    codeEl.querySelector('.custom_discount_box-code-title').innerHTML = code;
    if (amount) {
      codeEl.querySelector('.custom_discount_box-code-amount').innerHTML =
        `-${amount}`;
    } else {
      codeEl.querySelector('.custom_discount_box-code-amount').style.display =
        'none';
    }
    const remover = codeEl.querySelector('.custom_discount_box-code-remover');
    if (removable) {
      remover.addEventListener('click', (e) => this.onRemoveCode(e, code));
    } else {
      remover.remove();
    }
    return codeEl;
  };

  parseHTML = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    return doc.body.firstChild;
  };

  convertToMoney = ({ amount }) => {
    const value = amount / 100;
    const locale = `${window?.Shopify?.locale || 'en'}-${window?.Shopify?.country || 'US'}`;
    const currency = window?.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  initCartObserver = () => {
    const cartObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((rawEntry) => {
        const entry = rawEntry.toJSON();
        const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(
          entry.initiatorType,
        );
        const regex = /\/cart(\/(?!\.js\b)[^ ]*|$)/;
        const isCartChangeRequest = regex.test(entry.name);
        if (isValidRequestType && isCartChangeRequest) {
          this.checkCart();
        }
      });
    });

    cartObserver.observe({ entryTypes: ['resource'] });
  };
}

if (!customElements.get('custom-discount-box')) {
  customElements.define('custom-discount-box', CustomDiscountBox);
}
